import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import changePickUpWarehouse from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/ChangePickUpWarehouse";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import WarehouseAddressInfoDisplay, {
    WarehouseAddressInfoDisplayStyles,
} from "@insite/content-library/Components/WarehouseAddressInfoDisplay";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY } from "@store/theme/design";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    onWarehouseSelected: (warehouse: WarehouseModel) => void;
}

const mapStateToProps = (state: ApplicationState) => ({
    fulfillmentMethod: state.components.addressDrawer.fulfillmentMethod,
    pickUpWarehouse: state.context.session.pickUpWarehouse,
});

const mapDispatchToProps = {
    changePickUpWarehouse,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AlternateWarehousesSelectorStyles {
    wrapper?: InjectableCss;
    warehouseWrapper?: InjectableCss;
    headerText?: TypographyPresentationProps;
    changeLink?: LinkPresentationProps;
    warehouseAddress?: WarehouseAddressInfoDisplayStyles;
    findLocationModal?: FindLocationModalStyles;
    searchResultSelectButton?: ButtonPresentationProps;
}

export const alternateWarehousesSelectorStyles: AlternateWarehousesSelectorStyles = {
    headerText: {
        weight: 600,
    },
    changeLink: {
        css: css`
            display: none !important;
        `,
    },
    searchResultSelectButton: {
        variant: "secondary",
        sizeVariant: "small",
        css: css`
            padding: 0 2.2em;

            &,
            span {
                font-size: ${TYPOGRAPHY.size.smaller};
            }
        `,
    },
    warehouseWrapper: {
        css: css`
            padding-bottom: 1em;
            padding-left: 0.75em;
        `,
    },
};

const styles = alternateWarehousesSelectorStyles;

const AlternateWarehousesSelector = ({
    fulfillmentMethod,
    onWarehouseSelected,
    pickUpWarehouse,
    changePickUpWarehouse,
}: Props) => {
    const [alternateWarehouses, setAlternateWarehouses] = React.useState(pickUpWarehouse?.alternateWarehouses);
    const generateWarehouseSelectClickHandler = (warehouse: WarehouseModel) => {
        // changePickUpWarehouse({ warehouse });
        return () => onWarehouseSelected(warehouse);
    };

    if (!pickUpWarehouse) {
        return <></>;
    }

    // const alternateWarehouses = pickUpWarehouse.alternateWarehouses;

    useEffect(() => {
        setAlternateWarehouses(pickUpWarehouse.alternateWarehouses);
    }, [pickUpWarehouse, pickUpWarehouse.alternateWarehouses]);

    return (
        <>
            {alternateWarehouses ? (
                <StyledWrapper
                    id="alternateWarehousesSelector"
                    role="region"
                    aria-live="polite"
                    data-test-selector="changeCustomer_pickUpLocation"
                    {...styles.wrapper}
                >
                    {alternateWarehouses.length > 0 && (
                        <Typography {...styles.headerText}>{translate("Alternate Warehouses")}</Typography>
                    )}
                    {alternateWarehouses?.map(warehouse => (
                        <StyledWrapper key={warehouse.id} {...styles.warehouseWrapper}>
                            <WarehouseAddressInfoDisplay
                                warehouse={warehouse}
                                extendedStyles={styles.warehouseAddress}
                            />
                            <Button
                                type="button"
                                onClick={generateWarehouseSelectClickHandler(warehouse)}
                                data-test-selector="findLocationModal_warehouseSelect"
                                {...styles.searchResultSelectButton}
                            >
                                {translate("Select")}
                            </Button>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            ) : (
                <></>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AlternateWarehousesSelector);
